<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useOnboarding } from './useOnboarding';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import WahaAPI from 'dashboard/api/wahaIntegrations';
import AiAgentsAPI from 'dashboard/api/aiAgents';
import Modal from 'dashboard/components/Modal.vue';
import './driver-custom.css';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'completed']);

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { setInterfaceMode } = useOnboarding();
const { accountId } = useAccount();

const currentStep = ref(0);
const businessName = ref('');
const selectedMode = ref('simple');
const loading = ref(false);
const wahaSession = ref(null);
const qrCode = ref(null);
const connectionStatus = ref('pending');
const pollInterval = ref(null);
const showWizard = ref(props.show);

const totalSteps = 5;

const steps = computed(() => [
  { key: 'welcome', label: t('ONBOARDING_WIZARD.STEPS.WELCOME') },
  { key: 'qr', label: t('ONBOARDING_WIZARD.STEPS.QR') },
  { key: 'success', label: t('ONBOARDING_WIZARD.STEPS.SUCCESS') },
  { key: 'import', label: t('ONBOARDING_WIZARD.STEPS.IMPORT') },
  { key: 'ai_agent', label: t('ONBOARDING_WIZARD.STEPS.AI_AGENT') },
]);

const progressPercent = computed(() =>
  Math.round((currentStep.value / (totalSteps - 1)) * 100)
);

watch(
  () => props.show,
  val => {
    showWizard.value = val;
    if (val) currentStep.value = 0;
  }
);

const goToStep = index => {
  if (index >= 0 && index < totalSteps) {
    currentStep.value = index;
  }
};

const qrPollInterval = ref(null);

const fetchQRCode = async () => {
  if (!wahaSession.value?.name || qrCode.value) return;
  try {
    const response = await WahaAPI.getQRCode(wahaSession.value.name);
    if (
      response.data &&
      typeof response.data === 'string' &&
      response.data.length > 100
    ) {
      qrCode.value = response.data;
      if (qrPollInterval.value) clearInterval(qrPollInterval.value);
    }
  } catch {
    // QR not ready yet, will retry
  }
};

const startQRPolling = () => {
  if (qrPollInterval.value) clearInterval(qrPollInterval.value);
  qrPollInterval.value = setInterval(async () => {
    if (qrCode.value || connectionStatus.value === 'connected') {
      clearInterval(qrPollInterval.value);
      return;
    }
    await fetchQRCode();
  }, 3000);
};

const startPollingStatus = () => {
  if (pollInterval.value) clearInterval(pollInterval.value);
  pollInterval.value = setInterval(async () => {
    if (!wahaSession.value?.name || connectionStatus.value === 'connected') {
      clearInterval(pollInterval.value);
      return;
    }
    try {
      const response = await WahaAPI.getSessionStatus(wahaSession.value.name);
      const status = response.data?.status;
      if (
        status === 'CONNECTED' ||
        status === 'WORKING' ||
        status === 'AUTHENTICATED'
      ) {
        connectionStatus.value = 'connected';
        clearInterval(pollInterval.value);
        if (qrPollInterval.value) clearInterval(qrPollInterval.value);
        goToStep(2);
      }
    } catch {
      // Silently ignore polling errors
    }
  }, 3000);
};

const createWahaIntegration = async () => {
  loading.value = true;
  try {
    const response = await WahaAPI.create({
      inboxName:
        businessName.value || t('ONBOARDING_WIZARD.DEFAULT_INBOX_NAME'),
      deviceName: 'Principal',
      wahaServerUrl: window.chatwootConfig?.WAHA_SERVER_URL || '',
      wahaApiKey: window.chatwootConfig?.WAHA_API_KEY || '',
      chatwootUrl:
        window.chatwootConfig?.CHATWOOT_PUBLIC_URL || window.location.origin,
    });

    if (response.data.success) {
      wahaSession.value = {
        ...response.data.session,
        inboxId: response.data.inbox?.id,
      };
      await fetchQRCode();
      startQRPolling();
      startPollingStatus();
    } else {
      useAlert(
        response.data.error || t('ONBOARDING_WIZARD.ERRORS.WAHA_FAILED')
      );
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('ONBOARDING_WIZARD.ERRORS.WAHA_FAILED')
    );
  } finally {
    loading.value = false;
  }
};

const startSetup = async () => {
  if (!businessName.value.trim()) {
    useAlert(t('ONBOARDING_WIZARD.ERRORS.NAME_REQUIRED'));
    return;
  }
  await setInterfaceMode(selectedMode.value);
  goToStep(1);
  await createWahaIntegration();
};

const openConversations = () => {
  emit('completed');
  showWizard.value = false;
  router.push({
    name: 'home',
    params: { accountId: accountId.value },
  });
};

const skipWizard = () => {
  emit('close');
  showWizard.value = false;
};

const importStatus = ref({ contacts: null, messages: null });

const aiAgentTemplates = [
  {
    key: 'sales',
    icon: 'i-lucide-trending-up',
    catalogType: null,
    template: {
      system_prompt:
        'Sos un agente de ventas especializado en cerrar tratos por WhatsApp.',
      instructions:
        'Cualifica al cliente, entende sus necesidades y ofrece la mejor opcion.',
      welcome_message:
        'Hola! Soy tu asistente de ventas. En que puedo ayudarte hoy?',
      response_level: 3,
      temperature: 0.8,
      config: {
        flow_steps: [
          {
            type: 'action',
            title: 'Saludo',
            instruction:
              'Saluda al cliente y preguntá qué producto o servicio le interesa.',
          },
          {
            type: 'action',
            title: 'Cualificación',
            instruction:
              'Hacé preguntas para entender las necesidades del cliente.',
          },
          {
            type: 'action',
            title: 'Propuesta',
            instruction:
              'Presentá la mejor opción según lo que necesita el cliente.',
          },
          {
            type: 'action',
            title: 'Cierre',
            instruction:
              'Invitá al cliente a concretar la compra o agendar una demo.',
          },
        ],
      },
    },
  },
  {
    key: 'ecommerce',
    icon: 'i-lucide-shopping-cart',
    catalogType: 'products',
    template: {
      system_prompt:
        'Sos un asistente de e-commerce que ayuda a los clientes a encontrar productos.',
      instructions:
        'Busca productos del catálogo y recomienda los mas adecuados.',
      welcome_message:
        'Hola! Te ayudo a encontrar el producto que buscas. Que necesitas?',
      response_level: 3,
      temperature: 0.8,
      config: {
        flow_steps: [
          {
            type: 'action',
            title: 'Saludo',
            instruction: 'Saluda al cliente y preguntá qué producto busca.',
          },
          {
            type: 'product_search',
            title: 'Buscar producto',
            product_query: '{{user_input}}',
            include_media: true,
          },
          {
            type: 'action',
            title: 'Recomendación',
            instruction:
              'Recomendá el producto más adecuado y preguntá si quiere agregarlo al carrito.',
          },
        ],
      },
    },
  },
  {
    key: 'support',
    icon: 'i-lucide-headphones',
    catalogType: null,
    template: {
      system_prompt:
        'Sos un agente de soporte al cliente que resuelve dudas y problemas.',
      instructions:
        'Atiende consultas, resuelve problemas y ofrece transferir a un humano si es necesario.',
      welcome_message: 'Hola! Estoy aqui para ayudarte. Cual es tu consulta?',
      response_level: 3,
      temperature: 0.7,
      config: {
        flow_steps: [
          {
            type: 'action',
            title: 'Saludo',
            instruction: 'Saluda al cliente y preguntá en qué puedes ayudarle.',
          },
          {
            type: 'action',
            title: 'Resolver duda',
            instruction:
              'Respondé la consulta del cliente usando la información disponible. Si no tenés la respuesta, ofrecé transferir a un humano.',
          },
        ],
      },
    },
  },
  {
    key: 'appointments',
    icon: 'i-lucide-calendar',
    catalogType: null,
    template: {
      system_prompt: 'Sos un asistente de agendamiento de citas.',
      instructions:
        'Recoge los datos del cliente, fecha y hora preferida, y confirma la cita.',
      welcome_message:
        'Hola! Te ayudo a agendar una cita. Que servicio necesitas?',
      response_level: 3,
      temperature: 0.7,
      config: {
        flow_steps: [
          {
            type: 'action',
            title: 'Saludo',
            instruction:
              'Saluda al cliente y preguntá qué servicio quiere agendar.',
          },
          {
            type: 'action',
            title: 'Datos',
            instruction: 'Pedí nombre, fecha y hora preferida para la cita.',
          },
          {
            type: 'action',
            title: 'Confirmación',
            instruction: 'Confirmá la cita y enviá un resumen de los datos.',
          },
        ],
      },
    },
  },
  {
    key: 'restaurant',
    icon: 'i-lucide-utensils-crossed',
    catalogType: null,
    template: {
      system_prompt:
        'Sos un asistente de restaurante que toma pedidos y reservas.',
      instructions:
        'Saluda, toma el pedido o reserva y confirma con el cliente.',
      welcome_message:
        'Hola! Quieres hacer un pedido, reservar una mesa o ver el menu?',
      response_level: 3,
      temperature: 0.8,
      config: {
        flow_steps: [
          {
            type: 'action',
            title: 'Saludo',
            instruction:
              'Saludá al cliente y preguntá si quiere hacer un pedido, reservar una mesa o ver el menú.',
          },
          {
            type: 'action',
            title: 'Atender',
            instruction:
              'Tomá el pedido, la reserva o mostrá el menú según lo que solicite.',
          },
          {
            type: 'action',
            title: 'Confirmar',
            instruction: 'Confirmá el pedido o reserva y agradecé.',
          },
        ],
      },
    },
  },
  {
    key: 'real_estate',
    icon: 'i-lucide-home',
    catalogType: 'real_estate',
    template: {
      system_prompt:
        'Sos un asistente inmobiliario que ayuda a encontrar propiedades.',
      instructions: 'Pregunta requisitos, busca propiedades y agenda visitas.',
      welcome_message:
        'Hola! Te ayudo a encontrar la propiedad ideal. Que tipo de propiedad buscas?',
      response_level: 3,
      temperature: 0.7,
      config: {
        flow_steps: [
          {
            type: 'action',
            title: 'Saludo',
            instruction:
              'Saluda al cliente y preguntá qué tipo de propiedad busca.',
          },
          {
            type: 'action',
            title: 'Requisitos',
            instruction:
              'Preguntá presupuesto, zona, cantidad de dormitorios y baños.',
          },
          {
            type: 'product_search',
            title: 'Buscar propiedad',
            product_query: '{{user_input}}',
            include_media: true,
          },
          {
            type: 'action',
            title: 'Agendar visita',
            instruction: 'Ofrecé agendar una visita presencial o virtual.',
          },
        ],
      },
    },
  },
  {
    key: 'custom',
    icon: 'i-lucide-sliders-horizontal',
    catalogType: null,
    template: null,
  },
];

const selectedTemplate = ref(null);
const aiAgentName = ref('');
const isCreatingAgent = ref(false);
const aiAgentError = ref(false);
const aiAgentStep = ref('template');
const createdAgentId = ref(null);
const isSavingKnowledge = ref(false);
const knowledgeForm = ref({
  specialization: '',
  businessName: '',
  website: '',
  description: '',
});

const selectTemplate = templateKey => {
  selectedTemplate.value = templateKey;
  const template = aiAgentTemplates.find(item => item.key === templateKey);
  if (template && template.template) {
    aiAgentName.value = t(
      `ONBOARDING_WIZARD.AI_AGENT.TEMPLATES.${templateKey.toUpperCase()}.LABEL`
    );
  }
};

const createAiAgent = async () => {
  if (!selectedTemplate.value || !aiAgentName.value.trim()) return;
  isCreatingAgent.value = true;
  aiAgentError.value = false;
  try {
    const templateData = aiAgentTemplates.find(
      item => item.key === selectedTemplate.value
    );
    const payload = {
      name: aiAgentName.value.trim(),
      description: t(
        `ONBOARDING_WIZARD.AI_AGENT.TEMPLATES.${selectedTemplate.value.toUpperCase()}.DESCRIPTION`
      ),
      llm_provider: 'openai',
      llm_model: 'gpt-4o-mini',
    };
    if (templateData?.template) {
      payload.system_prompt = templateData.template.system_prompt;
      payload.instructions = templateData.template.instructions;
      payload.welcome_message = templateData.template.welcome_message;
      payload.response_level = templateData.template.response_level;
      payload.temperature = templateData.template.temperature;
      payload.config = templateData.template.config;
    }
    if (templateData?.catalogType) {
      payload.catalog_type = templateData.catalogType;
    }
    const response = await store.dispatch('aiAgents/create', payload);
    if (response?.id && wahaSession.value?.inboxId) {
      await AiAgentsAPI.addInbox(response.id, {
        inbox_id: wahaSession.value.inboxId,
        status: 0,
        priority: 0,
      });
    }
    if (response?.id) {
      createdAgentId.value = response.id;
      knowledgeForm.value.businessName = businessName.value || '';
      aiAgentStep.value = 'knowledge';
    }
  } catch (error) {
    aiAgentError.value = true;
    useAlert(t('ONBOARDING_WIZARD.AI_AGENT.CREATE_ERROR'));
  } finally {
    isCreatingAgent.value = false;
  }
};

const buildKnowledgeContent = () => {
  const k = knowledgeForm.value;
  const lines = [];
  if (k.specialization) lines.push(`Especialización: ${k.specialization}`);
  if (k.businessName) lines.push(`Nombre del negocio: ${k.businessName}`);
  if (k.website) lines.push(`Sitio web del negocio: ${k.website}`);
  if (k.description) lines.push(`Descripción del negocio: ${k.description}`);
  return lines.join('\n');
};

const saveKnowledge = async () => {
  if (!createdAgentId.value) return;
  isSavingKnowledge.value = true;
  try {
    const payload = {
      name:
        knowledgeForm.value.businessName || aiAgentName.value || 'Industria',
      source_type: 'text',
      content: buildKnowledgeContent(),
    };
    await AiAgentsAPI.createDocument(createdAgentId.value, payload);
    aiAgentStep.value = 'done';
  } catch (error) {
    useAlert(t('ONBOARDING_WIZARD.AI_AGENT.KNOWLEDGE_ERROR'));
  } finally {
    isSavingKnowledge.value = false;
  }
};

const skipKnowledge = () => {
  aiAgentStep.value = 'done';
};

const sendWahaCommand = async commandType => {
  if (!wahaSession.value?.inboxId) return;
  importStatus.value[commandType] = 'loading';
  try {
    const command =
      commandType === 'contacts'
        ? 'wa/contacts pull'
        : 'wa/messages pull 5d 0d';
    await WahaAPI.sendCommand(wahaSession.value.inboxId, command);
    importStatus.value[commandType] = 'sent';
  } catch (error) {
    importStatus.value[commandType] = 'error';
    useAlert(error.response?.data?.error || 'Error al enviar comando');
  }
};

onUnmounted(() => {
  if (pollInterval.value) clearInterval(pollInterval.value);
});
</script>

<template>
  <Modal v-model:show="showWizard" size="medium" :on-close="skipWizard">
    <div class="flex h-[640px] w-full max-w-[860px]">
      <!-- Sidebar progress -->
      <div class="w-64 bg-n-surface-2 border-r border-n-weak flex flex-col p-5">
        <div class="mb-6">
          <div class="flex items-center justify-center">
            <svg
              width="32"
              height="32"
              viewBox="0 0 512 512"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="256" cy="256" r="256" fill="#2781F6" />
              <path
                d="M362.808 368.808H244.123C178.699 368.808 125.457 315.562 125.457 250.122C125.457 184.703 178.699 131.457 244.124 131.457C309.565 131.457 362.808 184.703 362.808 250.122V368.808Z"
                stroke="white"
                stroke-width="12"
                fill="white"
              />
            </svg>
          </div>
        </div>

        <div class="flex-1 flex flex-col gap-1">
          <div
            v-for="(step, index) in steps"
            :key="step.key"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors"
            :class="
              index === currentStep
                ? 'bg-n-brand/10 text-n-brand font-medium'
                : index < currentStep
                  ? 'text-n-slate-11'
                  : 'text-n-slate-10'
            "
          >
            <span
              class="size-6 rounded-full flex items-center justify-center text-xs flex-shrink-0"
              :class="
                index < currentStep
                  ? 'bg-n-brand text-white'
                  : index === currentStep
                    ? 'bg-n-brand/20 text-n-brand'
                    : 'bg-n-weak text-n-slate-10'
              "
            >
              <span v-if="index < currentStep">{{ '\u2713' }}</span>
              <span v-else>{{ index + 1 }}</span>
            </span>
            <span>{{ step.label }}</span>
          </div>
        </div>

        <div class="mt-4">
          <div class="h-1.5 bg-n-weak rounded-full overflow-hidden">
            <div
              class="h-full bg-n-brand rounded-full transition-all duration-500"
              :style="{ width: `${progressPercent}%` }"
            />
          </div>
          <p class="text-xs text-n-slate-11 mt-2 text-center">
            {{
              t('ONBOARDING_WIZARD.PROGRESS', {
                current: currentStep + 1,
                total: totalSteps,
              })
            }}
          </p>
        </div>
      </div>

      <!-- Content area -->
      <div class="flex-1 flex flex-col p-8 overflow-y-auto">
        <!-- Step 1: Welcome -->
        <div v-if="currentStep === 0" class="flex flex-col h-full">
          <div class="flex-1 flex flex-col items-center justify-center gap-6">
            <div class="text-5xl">{{ '\uD83D\uDC4B' }}</div>
            <div class="text-center">
              <h2 class="text-2xl font-bold text-n-slate-12 mb-2">
                {{ t('ONBOARDING_WIZARD.WELCOME.TITLE') }}
              </h2>
              <p class="text-n-slate-11 max-w-sm">
                {{ t('ONBOARDING_WIZARD.WELCOME.SUBTITLE') }}
              </p>
            </div>

            <div class="w-full max-w-sm space-y-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('ONBOARDING_WIZARD.WELCOME.BUSINESS_NAME_LABEL') }}
                </label>
                <input
                  v-model="businessName"
                  type="text"
                  :placeholder="
                    t('ONBOARDING_WIZARD.WELCOME.BUSINESS_NAME_PLACEHOLDER')
                  "
                  class="w-full px-4 py-2.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:ring-2 focus:ring-n-brand focus:border-transparent"
                />
                <p class="text-xs text-n-slate-11 mt-1">
                  {{ t('ONBOARDING_WIZARD.WELCOME.BUSINESS_NAME_HELP') }}
                </p>
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('ONBOARDING_WIZARD.WELCOME.MODE_LABEL') }}
                </label>
                <div class="flex gap-2">
                  <button
                    class="flex-1 py-2.5 px-3 rounded-lg border text-sm font-medium transition-all"
                    :class="
                      selectedMode === 'simple'
                        ? 'border-n-brand bg-n-brand/10 text-n-brand'
                        : 'border-n-weak text-n-slate-11 hover:bg-n-surface-3'
                    "
                    @click="selectedMode = 'simple'"
                  >
                    {{ t('ONBOARDING_WIZARD.WELCOME.MODE_SIMPLE') }}
                  </button>
                  <button
                    class="flex-1 py-2.5 px-3 rounded-lg border text-sm font-medium transition-all"
                    :class="
                      selectedMode === 'expert'
                        ? 'border-n-brand bg-n-brand/10 text-n-brand'
                        : 'border-n-weak text-n-slate-11 hover:bg-n-surface-3'
                    "
                    @click="selectedMode = 'expert'"
                  >
                    {{ t('ONBOARDING_WIZARD.WELCOME.MODE_EXPERT') }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-between items-center pt-6">
            <button
              class="text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
              @click="skipWizard"
            >
              {{ t('ONBOARDING_WIZARD.SKIP') }}
            </button>
            <button
              class="px-6 py-2.5 rounded-lg bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 transition-colors disabled:opacity-50"
              :disabled="loading"
              @click="startSetup"
            >
              <span v-if="loading" class="flex items-center gap-2">
                <svg
                  class="w-4 h-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {{ t('ONBOARDING_WIZARD.LOADING') }}
              </span>
              <span v-else>{{ t('ONBOARDING_WIZARD.START') }}</span>
            </button>
          </div>
        </div>

        <!-- Step 2: QR Code -->
        <div v-else-if="currentStep === 1" class="flex flex-col h-full">
          <div class="flex-1 flex flex-col items-center justify-center gap-6">
            <div class="text-center">
              <h2 class="text-2xl font-bold text-n-slate-12 mb-2">
                {{ t('ONBOARDING_WIZARD.QR.TITLE') }}
              </h2>
              <p class="text-n-slate-11 max-w-sm">
                {{ t('ONBOARDING_WIZARD.QR.SUBTITLE') }}
              </p>
            </div>

            <div
              class="w-64 h-64 rounded-2xl border-2 border-n-weak bg-white flex items-center justify-center overflow-hidden"
            >
              <img
                v-if="qrCode"
                :src="`data:image/png;base64,${qrCode}`"
                alt="QR Code"
                class="w-full h-full object-contain"
              />
              <div v-else-if="loading" class="flex flex-col items-center gap-2">
                <svg
                  class="w-8 h-8 animate-spin text-n-brand"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span class="text-sm text-n-slate-11">{{
                  t('ONBOARDING_WIZARD.QR.GENERATING')
                }}</span>
              </div>
              <div v-else class="text-sm text-n-slate-11">
                {{ t('ONBOARDING_WIZARD.QR.ERROR') }}
              </div>
            </div>

            <div class="flex items-center gap-2 text-sm text-n-slate-11">
              <span
                class="size-2 rounded-full"
                :class="
                  connectionStatus === 'connected'
                    ? 'bg-green-500'
                    : 'bg-yellow-500 animate-pulse'
                "
              />
              <span>
                {{
                  connectionStatus === 'connected'
                    ? t('ONBOARDING_WIZARD.QR.CONNECTED')
                    : t('ONBOARDING_WIZARD.QR.WAITING')
                }}
              </span>
            </div>

            <button
              class="text-sm text-n-brand hover:underline"
              @click="goToStep(2)"
            >
              {{ t('ONBOARDING_WIZARD.QR.ALREADY_SCANNED') }}
            </button>
          </div>
        </div>

        <!-- Step 3: Success -->
        <div v-else-if="currentStep === 2" class="flex flex-col h-full">
          <div class="flex-1 flex flex-col items-center justify-center gap-6">
            <div class="text-5xl">{{ '\uD83C\uDF89' }}</div>
            <div class="text-center">
              <h2 class="text-2xl font-bold text-n-slate-12 mb-2">
                {{ t('ONBOARDING_WIZARD.SUCCESS.TITLE') }}
              </h2>
              <p class="text-n-slate-11 max-w-sm">
                {{ t('ONBOARDING_WIZARD.SUCCESS.SUBTITLE') }}
              </p>
            </div>

            <div class="w-full max-w-sm space-y-3">
              <div
                class="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200"
              >
                <span class="text-green-600 text-lg">{{ '\u2713' }}</span>
                <span class="text-sm text-green-800">{{
                  t('ONBOARDING_WIZARD.SUCCESS.WHATSAPP_CONNECTED')
                }}</span>
              </div>
              <div
                class="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200"
              >
                <span class="text-green-600 text-lg">{{ '\u2713' }}</span>
                <span class="text-sm text-green-800">{{
                  t('ONBOARDING_WIZARD.SUCCESS.INBOX_CREATED')
                }}</span>
              </div>
              <div
                class="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200"
              >
                <span class="text-green-600 text-lg">{{ '\u2713' }}</span>
                <span class="text-sm text-green-800">{{
                  t('ONBOARDING_WIZARD.SUCCESS.AGENT_ASSIGNED')
                }}</span>
              </div>
            </div>
          </div>

          <div class="flex justify-end pt-6">
            <button
              class="px-6 py-2.5 rounded-lg bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 transition-colors"
              @click="goToStep(3)"
            >
              {{ t('ONBOARDING_WIZARD.SUCCESS.CONTINUE') }}
            </button>
          </div>
        </div>

        <!-- Step 4: Import -->
        <div v-else-if="currentStep === 3" class="flex flex-col h-full">
          <div class="flex-1 flex flex-col items-center justify-center gap-6">
            <div class="text-center">
              <h2 class="text-2xl font-bold text-n-slate-12 mb-2">
                {{ t('ONBOARDING_WIZARD.IMPORT.TITLE') }}
              </h2>
              <p class="text-n-slate-11 max-w-sm">
                {{ t('ONBOARDING_WIZARD.IMPORT.SUBTITLE') }}
              </p>
            </div>

            <div class="w-full max-w-sm space-y-4">
              <!-- Import Contacts Button -->
              <button
                class="w-full flex items-center gap-3 p-4 rounded-xl border border-n-weak bg-n-surface-2 hover:bg-n-surface-3 transition-colors text-left"
                :disabled="
                  importStatus.contacts === 'loading' ||
                  importStatus.contacts === 'sent'
                "
                @click="sendWahaCommand('contacts')"
              >
                <div
                  class="w-10 h-10 rounded-lg bg-n-brand/10 flex items-center justify-center text-n-brand"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="text-sm font-medium text-n-slate-12">
                    {{ t('ONBOARDING_WIZARD.IMPORT.CONTACTS_BUTTON') }}
                  </div>
                  <div class="text-xs text-n-slate-11">
                    {{
                      importStatus.contacts === 'sent'
                        ? t('ONBOARDING_WIZARD.IMPORT.SENT')
                        : t('ONBOARDING_WIZARD.IMPORT.CONTACTS_DESC')
                    }}
                  </div>
                </div>
                <span
                  v-if="importStatus.contacts === 'loading'"
                  class="w-5 h-5 border-2 border-n-brand border-t-transparent rounded-full animate-spin"
                />
                <span
                  v-else-if="importStatus.contacts === 'sent'"
                  class="text-green-500 text-lg"
                >
                  {{ '\u2713' }}
                </span>
              </button>

              <!-- Import Messages Button -->
              <button
                class="w-full flex items-center gap-3 p-4 rounded-xl border border-n-weak bg-n-surface-2 hover:bg-n-surface-3 transition-colors text-left"
                :disabled="
                  importStatus.messages === 'loading' ||
                  importStatus.messages === 'sent'
                "
                @click="sendWahaCommand('messages')"
              >
                <div
                  class="w-10 h-10 rounded-lg bg-n-brand/10 flex items-center justify-center text-n-brand"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                    />
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="text-sm font-medium text-n-slate-12">
                    {{ t('ONBOARDING_WIZARD.IMPORT.MESSAGES_BUTTON') }}
                  </div>
                  <div class="text-xs text-n-slate-11">
                    {{
                      importStatus.messages === 'sent'
                        ? t('ONBOARDING_WIZARD.IMPORT.SENT')
                        : t('ONBOARDING_WIZARD.IMPORT.MESSAGES_DESC')
                    }}
                  </div>
                </div>
                <span
                  v-if="importStatus.messages === 'loading'"
                  class="w-5 h-5 border-2 border-n-brand border-t-transparent rounded-full animate-spin"
                />
                <span
                  v-else-if="importStatus.messages === 'sent'"
                  class="text-green-500 text-lg"
                >
                  {{ '\u2713' }}
                </span>
              </button>

              <p class="text-xs text-n-slate-11 text-center px-2">
                {{ t('ONBOARDING_WIZARD.IMPORT.HINT') }}
              </p>
            </div>
          </div>

          <div class="flex justify-between pt-6">
            <button
              class="text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
              @click="goToStep(2)"
            >
              {{ t('ONBOARDING_WIZARD.BACK') }}
            </button>
            <button
              class="px-6 py-2.5 rounded-lg bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 transition-colors"
              @click="goToStep(4)"
            >
              {{ t('ONBOARDING_WIZARD.SUCCESS.CONTINUE') }}
            </button>
          </div>
        </div>

        <!-- Step 5: AI Agent -->
        <div v-else-if="currentStep === 4" class="flex flex-col h-full">
          <div class="flex-1 flex flex-col items-center gap-6 overflow-y-auto">
            <div class="text-center">
              <h2 class="text-2xl font-bold text-n-slate-12 mb-2">
                {{
                  aiAgentStep === 'knowledge'
                    ? t('ONBOARDING_WIZARD.AI_AGENT.KNOWLEDGE_TITLE')
                    : t('ONBOARDING_WIZARD.AI_AGENT.TITLE')
                }}
              </h2>
              <p class="text-n-slate-11 max-w-lg">
                {{
                  aiAgentStep === 'knowledge'
                    ? t('ONBOARDING_WIZARD.AI_AGENT.KNOWLEDGE_SUBTITLE')
                    : t('ONBOARDING_WIZARD.AI_AGENT.SUBTITLE')
                }}
              </p>
            </div>

            <!-- Done state -->
            <div
              v-if="aiAgentStep === 'done'"
              class="w-full max-w-sm space-y-3"
            >
              <div
                class="flex items-center gap-3 p-4 rounded-lg bg-green-50 border border-green-200"
              >
                <span class="text-green-600 text-lg">{{ '\u2713' }}</span>
                <span class="text-sm text-green-800">{{
                  t('ONBOARDING_WIZARD.AI_AGENT.CREATED')
                }}</span>
              </div>
            </div>

            <!-- Knowledge form -->
            <div
              v-else-if="aiAgentStep === 'knowledge'"
              class="w-full space-y-4"
            >
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">{{
                  t('AI_AGENTS.KNOWLEDGE.SPECIALIZATION')
                }}</span>
                <input
                  v-model="knowledgeForm.specialization"
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-2 text-n-slate-12 focus:border-n-brand focus:outline-none"
                  :placeholder="
                    t('AI_AGENTS.KNOWLEDGE.SPECIALIZATION_PLACEHOLDER')
                  "
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">{{
                  t('AI_AGENTS.KNOWLEDGE.BUSINESS_NAME')
                }}</span>
                <input
                  v-model="knowledgeForm.businessName"
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-2 text-n-slate-12 focus:border-n-brand focus:outline-none"
                  :placeholder="
                    t('AI_AGENTS.KNOWLEDGE.BUSINESS_NAME_PLACEHOLDER')
                  "
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">{{
                  t('AI_AGENTS.KNOWLEDGE.BUSINESS_WEBSITE')
                }}</span>
                <input
                  v-model="knowledgeForm.website"
                  type="url"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-2 text-n-slate-12 focus:border-n-brand focus:outline-none"
                  :placeholder="
                    t('AI_AGENTS.KNOWLEDGE.BUSINESS_WEBSITE_PLACEHOLDER')
                  "
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">{{
                  t('AI_AGENTS.KNOWLEDGE.BUSINESS_DESCRIPTION')
                }}</span>
                <textarea
                  v-model="knowledgeForm.description"
                  rows="4"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-2 text-n-slate-12 focus:border-n-brand focus:outline-none resize-none"
                  :placeholder="
                    t('AI_AGENTS.KNOWLEDGE.BUSINESS_DESCRIPTION_PLACEHOLDER')
                  "
                />
              </label>
            </div>

            <!-- Template selection -->
            <div v-else class="w-full space-y-4">
              <div class="grid grid-cols-3 gap-3">
                <button
                  v-for="template in aiAgentTemplates"
                  :key="template.key"
                  class="flex flex-col items-start gap-2 p-4 rounded-xl border text-left transition-colors"
                  :class="
                    selectedTemplate === template.key
                      ? 'border-n-brand bg-n-brand/5'
                      : 'border-n-weak bg-n-surface-2 hover:bg-n-surface-3'
                  "
                  @click="selectTemplate(template.key)"
                >
                  <span :class="template.icon" class="w-6 h-6 text-n-brand" />
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      t(
                        `ONBOARDING_WIZARD.AI_AGENT.TEMPLATES.${template.key.toUpperCase()}.LABEL`
                      )
                    }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{
                      t(
                        `ONBOARDING_WIZARD.AI_AGENT.TEMPLATES.${template.key.toUpperCase()}.DESCRIPTION`
                      )
                    }}
                  </span>
                </button>
              </div>

              <!-- Agent name input -->
              <div v-if="selectedTemplate" class="space-y-2 max-w-md">
                <label class="text-xs font-medium text-n-slate-11">
                  {{ t('ONBOARDING_WIZARD.AI_AGENT.AGENT_NAME_LABEL') }}
                </label>
                <input
                  v-model="aiAgentName"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-2 text-n-slate-12 focus:border-n-brand focus:outline-none"
                  :placeholder="
                    t('ONBOARDING_WIZARD.AI_AGENT.AGENT_NAME_PLACEHOLDER')
                  "
                />
              </div>
            </div>
          </div>

          <div class="flex justify-between pt-6">
            <button
              class="text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
              @click="goToStep(3)"
            >
              {{ t('ONBOARDING_WIZARD.BACK') }}
            </button>
            <div class="flex items-center gap-3">
              <button
                v-if="aiAgentStep !== 'done'"
                class="text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
                @click="
                  aiAgentStep === 'knowledge'
                    ? skipKnowledge()
                    : openConversations()
                "
              >
                {{ t('ONBOARDING_WIZARD.AI_AGENT.SKIP') }}
              </button>
              <button
                v-if="aiAgentStep === 'done'"
                class="px-6 py-2.5 rounded-lg bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 transition-colors"
                @click="openConversations"
              >
                {{ t('ONBOARDING_WIZARD.AI_AGENT.FINISH') }}
              </button>
              <button
                v-else-if="aiAgentStep === 'knowledge'"
                :disabled="isSavingKnowledge"
                class="px-6 py-2.5 rounded-lg bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                @click="saveKnowledge"
              >
                <span
                  v-if="isSavingKnowledge"
                  class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                />
                {{
                  isSavingKnowledge
                    ? t('ONBOARDING_WIZARD.AI_AGENT.SAVING_KNOWLEDGE')
                    : t('ONBOARDING_WIZARD.AI_AGENT.SAVE_KNOWLEDGE')
                }}
              </button>
              <button
                v-else
                :disabled="
                  !selectedTemplate || !aiAgentName.trim() || isCreatingAgent
                "
                class="px-6 py-2.5 rounded-lg bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                @click="createAiAgent"
              >
                <span
                  v-if="isCreatingAgent"
                  class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                />
                {{
                  isCreatingAgent
                    ? t('ONBOARDING_WIZARD.AI_AGENT.CREATING')
                    : t('ONBOARDING_WIZARD.AI_AGENT.CREATE_AND_FINISH')
                }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>
